import type Iot from 'aws-sdk/clients/iot'
import type { IotData } from 'aws-sdk'
import { DeviceThing } from '../../types/DeviceThing.types'
import { Shadow } from '../../types/Shadow.types'
import { createEmptyDeviceThing } from './deviceThingDefaults'
import { applyShadowResults, fetchDeviceShadows } from './iotShadow'
import { shadowNames } from './configuration'

// Browsers open at most 6 HTTP/1.1 connections per host and the IoT data endpoint has no HTTP/2, so more parallel reads only queue.
const MAX_PARALLEL_READS = 6

function checkAborted(signal?: AbortSignal) {
    if (signal?.aborted) throw new DOMException('Przerwano pobieranie.', 'AbortError')
}

function toDevice(thing: Pick<Iot.ThingAttribute, 'thingName' | 'thingArn' | 'thingTypeName' | 'version'>): DeviceThing {
    return {
        ...createEmptyDeviceThing(),
        thingName: thing.thingName,
        thingArn: thing.thingArn,
        thingTypeName: thing.thingTypeName,
        version: thing.version,
    }
}

export const hasReadShadow = (device: DeviceThing, shadow: Shadow) => Boolean(device.shadowDocuments[shadow] || device.shadowErrors[shadow])
export const hasReadAllShadows = (device: DeviceThing) => shadowNames.every((shadow) => hasReadShadow(device, shadow))

// Lists the whole fleet without shadows: a few requests, enough to show and search the list by serial number.
export async function listIoTThings(iot: Iot, signal?: AbortSignal): Promise<DeviceThing[]> {
    const things: Iot.ThingAttribute[] = []
    let nextToken: string | undefined
    do {
        checkAborted(signal)
        const page = await iot.listThings({ maxResults: 250, ...(nextToken ? { nextToken } : {}) }).promise()
        things.push(...(page.things || []))
        nextToken = page.nextToken
    } while (nextToken)
    checkAborted(signal)
    return things
        .filter((thing) => thing.thingName)
        .map(toDevice)
        .sort((a, b) => a.thingName!.localeCompare(b.thingName!))
}

// Reads the shadows of listed devices in the background and reports every updated device.
// Order: devices on screen, then the datasyncd shadow of every device (the instance used by search), then the remaining
// shadows device by device, so last update times become final in list order.
export async function loadDeviceShadows(
    devices: DeviceThing[],
    iotData: IotData,
    onLoaded: (device: DeviceThing) => void,
    { signal, isVisible = () => false }: { signal?: AbortSignal; isVisible?: (thingName: string) => boolean } = {}
): Promise<void> {
    const latest = new Map(devices.map((device) => [device.thingName!, device]))
    const names = Array.from(latest.keys())
    const otherShadows = shadowNames.filter((shadow) => shadow !== Shadow.Datasyncd)
    const pending: [string, Shadow][] = [
        ...names.map((name): [string, Shadow] => [name, Shadow.Datasyncd]),
        ...names.flatMap((name) => otherShadows.map((shadow): [string, Shadow] => [name, shadow])),
    ]
    const worker = async () => {
        while (pending.length) {
            checkAborted(signal)
            const visibleIndex = pending.findIndex(([thingName]) => isVisible(thingName))
            const [[name, shadow]] = pending.splice(Math.max(visibleIndex, 0), 1)
            const results = await fetchDeviceShadows(name, iotData, [shadow])
            checkAborted(signal)
            // Apply to the latest state: other shadows of the same device are read in parallel.
            const device = applyShadowResults(latest.get(name)!, results)
            latest.set(name, device)
            onLoaded(device)
        }
    }
    await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL_READS, pending.length) }, worker))
}

export async function fetchIoTDevice(thingName: string, iot: Iot, iotData: IotData): Promise<DeviceThing> {
    // Thing metadata is informational only: without iot:DescribeThing the configuration must still open.
    const described = await iot
        .describeThing({ thingName })
        .promise()
        .catch(() => undefined)
    const device = toDevice({ ...described, thingName })
    return applyShadowResults(device, await fetchDeviceShadows(thingName, iotData, shadowNames, true))
}
